// Filter builder over the shared filter language (R-C4): conditions on fields, terms, owners and related records, joined by «и»/«или».

import { useState } from 'react';
import { FIELD_TYPES, peopleFields, recordTitle, type Condition, type FieldDef, type Filter, type FilterOp, type Id } from '@confdeck/core-shared';
import { RecordPicker, TermPicker, UserPicker } from '../fields/pickers.tsx';
import { useMeta, useSync } from '../sync/react.tsx';
import { FIELD_ICONS, Icon } from '../ui/icons.tsx';
import { IconButton, Button, Chip, Input, Menu, MenuContent, MenuItem, MenuTrigger, Popover, PopoverContent, PopoverTrigger } from '../ui/primitives.tsx';
import { DateInput } from '../ui/DateInput.tsx';
import s from './toolbar.module.css';

export const OP_LABEL: Record<FilterOp, string> = {
  eq: 'равно', neq: 'не равно', in: 'любое из', nin: 'ни одно из', contains: 'содержит', ncontains: 'не содержит', starts: 'начинается с',
  empty: 'пусто', nempty: 'не пусто', gt: 'больше', gte: 'не меньше', lt: 'меньше', lte: 'не больше', under: 'внутри', some: 'любая из', none: 'ни одна из',
};
const NO_VALUE: FilterOp[] = ['empty', 'nempty'];
const SYS_FIELDS: Record<string, { name: string; ops: FilterOp[]; kind: 'user' | 'date' }> = {
  ownerId: { name: 'Ответственный', ops: ['in', 'nin', 'empty', 'nempty'], kind: 'user' },
  createdAt: { name: 'Создано', ops: ['gte', 'lte'], kind: 'date' },
  updatedAt: { name: 'Изменено', ops: ['gte', 'lte'], kind: 'date' },
};

/** The builder edits one level: a list of conditions joined by one operator. */
export interface FlatFilter { join: 'and' | 'or'; items: Condition[] }

export function toFlat(f: Filter | null): FlatFilter {
  if (!f) return { join: 'and', items: [] };
  if ('and' in f) return { join: 'and', items: f.and.filter((x): x is Condition => 'field' in x) };
  if ('or' in f) return { join: 'or', items: f.or.filter((x): x is Condition => 'field' in x) };
  if ('field' in f) return { join: 'and', items: [f] };
  return { join: 'and', items: [] };
}
export function fromFlat(ff: FlatFilter): Filter | null {
  const filled = (v: unknown) => v !== undefined && v !== '' && !(Array.isArray(v) && !v.length);
  const items = ff.items.filter((c) => NO_VALUE.includes(c.op) || (c.where ? 'field' in c.where && filled(c.where.value) : filled(c.value)));
  if (!items.length) return null;
  return ff.join === 'and' ? { and: items } : { or: items };
}

function defaultOp(f: FieldDef | undefined, sys?: string): FilterOp {
  if (sys) return SYS_FIELDS[sys]!.ops[0]!;
  if (!f) return 'contains';
  if (f.fieldType === 'taxonomy') return 'in';
  if (f.fieldType === 'relation') return 'some';
  if (f.fieldType === 'user') return 'in';
  return FIELD_TYPES[f.fieldType].ops[0]!;
}

export function FilterBuilder({ typeId, value, onChange }: { typeId: Id; value: FlatFilter; onChange: (v: FlatFilter) => void }) {
  const meta = useMeta();
  const fields = peopleFields(meta.fields(typeId));
  const set = (i: number, c: Condition) => onChange({ ...value, items: value.items.map((x, j) => (j === i ? c : x)) });
  const remove = (i: number) => onChange({ ...value, items: value.items.filter((_, j) => j !== i) });
  const add = (key: string) => {
    const f = meta.field(typeId, key);
    onChange({ ...value, items: [...value.items, { field: key, op: defaultOp(f, SYS_FIELDS[key] ? key : undefined) }] });
  };
  return (
    <div className={s.builder}>
      {!value.items.length && <div className={s.hint}>Фильтров нет. Добавьте условие — оно сразу сужает список.</div>}
      {value.items.map((c, i) => (
        <div key={i} className={s.condRow}>
          <span className={s.join}>
            {i === 0 ? 'Где' : i === 1 ? (
              <Button size="sm" variant="secondary" onClick={() => onChange({ ...value, join: value.join === 'and' ? 'or' : 'and' })}>{value.join === 'and' ? 'и' : 'или'}</Button>
            ) : value.join === 'and' ? 'и' : 'или'}
          </span>
          <CondEditor typeId={typeId} cond={c} onChange={(nc) => set(i, nc)} />
          <IconButton size="sm" icon="close" label="Убрать условие" onClick={() => remove(i)} />
        </div>
      ))}
      <Menu>
        <MenuTrigger asChild><Button size="sm" icon="plus">Добавить условие</Button></MenuTrigger>
        <MenuContent>
          {fields.map((f) => <MenuItem key={f.id} icon={FIELD_ICONS[f.fieldType]} onSelect={() => add(f.key)}>{f.name}</MenuItem>)}
          {Object.entries(SYS_FIELDS).map(([k, v]) => <MenuItem key={k} icon={v.kind === 'user' ? 'user' : 'date'} onSelect={() => add(k)}>{v.name}</MenuItem>)}
        </MenuContent>
      </Menu>
    </div>
  );
}

function CondEditor({ typeId, cond, onChange }: { typeId: Id; cond: Condition; onChange: (c: Condition) => void }) {
  const meta = useMeta();
  const f = meta.field(typeId, cond.field);
  const sys = SYS_FIELDS[cond.field];
  const ops = sys ? sys.ops : f ? FIELD_TYPES[f.fieldType].ops : [];
  return (
    <>
      <span className={s.condField}><Icon name={f ? FIELD_ICONS[f.fieldType]! : sys?.kind === 'user' ? 'user' : 'date'} size={13} />{f?.name ?? sys?.name}</span>
      <Menu>
        <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{OP_LABEL[cond.op]}</Button></MenuTrigger>
        <MenuContent>{ops.map((op) => <MenuItem key={op} onSelect={() => onChange({ field: cond.field, op })}>{OP_LABEL[op]}</MenuItem>)}</MenuContent>
      </Menu>
      {!NO_VALUE.includes(cond.op) && <ValueEditor field={f} sysKind={sys?.kind} cond={cond} onChange={onChange} />}
    </>
  );
}

function ValueEditor({ field, sysKind, cond, onChange }: { field?: FieldDef; sysKind?: 'user' | 'date'; cond: Condition; onChange: (c: Condition) => void }) {
  const meta = useMeta();
  const { store } = useSync();
  const [open, setOpen] = useState(!cond.value && !cond.where);
  const kind = sysKind ?? field?.fieldType;
  if (kind === 'date' || kind === 'datetime') {
    return <DateInput className={s.dateInput} value={String(cond.value ?? '').slice(0, 10)} onChange={(v) => onChange({ ...cond, value: v })} />;
  }
  if (kind === 'checkbox') {
    return <Button size="sm" variant="secondary" onClick={() => onChange({ ...cond, value: !cond.value })}>{cond.value ? 'да' : 'нет'}</Button>;
  }
  if (kind === 'taxonomy' || kind === 'user' || kind === 'relation') {
    const list = kind === 'relation'
      ? ((cond.where && 'field' in cond.where && Array.isArray(cond.where.value)) ? cond.where.value as string[] : [])
      : (Array.isArray(cond.value) ? cond.value : cond.value ? [cond.value] : []) as string[];
    const label = list.length
      ? list.slice(0, 2).map((id) => kind === 'relation' ? recordTitle(store.record(id), meta) : kind === 'user' ? meta.user(id)?.name : meta.term(id)?.name).join(', ') + (list.length > 2 ? ` +${list.length - 2}` : '')
      : 'выбрать…';
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild><Button size="sm" variant="secondary" className={s.valueBtn}>{kind === 'taxonomy' && list.length === 1 ? <Chip color={meta.term(list[0]!)?.color}>{label}</Chip> : label}</Button></PopoverTrigger>
        <PopoverContent width={280}>
          {kind === 'taxonomy' && field && <TermPicker field={{ ...field, options: { ...field.options, multiple: true } }} value={list} onChange={(v) => onChange({ ...cond, value: v })} />}
          {kind === 'user' && <UserPicker value={list[0]} onChange={(v) => onChange({ ...cond, value: v ? [v] : [] })} onDone={() => setOpen(false)} />}
          {kind === 'relation' && field && <RecordPicker field={field} multipleOverride value={list} onChange={(ids) => onChange({ ...cond, where: { field: 'id', op: 'in', value: ids } })} />}
        </PopoverContent>
      </Popover>
    );
  }
  const isNum = kind === 'number' || kind === 'money';
  const shown = isNum && kind === 'money' && typeof cond.value === 'number' ? String(cond.value / 100) : String(cond.value ?? '');
  return (
    <Input autoFocus={!cond.value} className={s.valueInput} value={shown} placeholder="значение"
      onChange={(e) => {
        const t = e.target.value;
        const v = isNum ? (t === '' ? '' : kind === 'money' ? Math.round(Number(t.replace(',', '.')) * 100) : Number(t.replace(',', '.'))) : t;
        onChange({ ...cond, value: v });
      }} />
  );
}
